import React, { useMemo } from "react";
import { useTable } from "react-table";
import Loader from "./Loader";

interface BranchColor {
  bgColor: string;
  textColor: string;
  borderColor: string;
}

interface TableProps<T extends object> {
  columns: any[];
  data: T[];
  tableHead: string;
  type: string;
  loading: boolean;
  isComparative?: boolean;
  branchCount?: number;
  branchColors?: Record<string, BranchColor>;
  branchIds?: string[];
}

const Table3 = <T extends object>({
  columns,
  data,
  tableHead,
  type,
  loading,
  isComparative = false,
  branchCount = 1,
  branchColors = {},
  branchIds = [],
}: TableProps<T>) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<T>({ columns, data });

  const fixedColumnCount = useMemo(() => {
    let count = 0;
    for (const col of columns) {
      const header = typeof col.Header === "string" ? col.Header : "";
      if (header.includes("(") && header.includes(")")) break;
      count++;
    }
    return count;
  }, [columns]);

  const getMappedValue = (columnId: string, value: string) => {
    const mappings: Record<string, Record<string, string>> = {
      category: {
        "0": " ",
        "1": "EMI Loans",
        "2": "EWI Loans(ntb)",
        "3": "EWI Loans(etb)",
        "4": "Past Customers",
        "5": "EWI Loans",
      },
      releationType: {
        "0": " ",
        "1": "% Disbursed Amount",
        "2": "% Loan Count",
        "3": "New-to-Bank (NTB)",
        "4": "Existing-to-KPB",
        "5": "Past-KPB",
      },
      state: {
        "0": " ",
        "1": "Kerala",
        "2": "Tamil Nadu",
      },
      stateEntity: {
        "0": " ",
        "1": "# Loans",
        "2": "% Loans",
        "3": "Disb Amt",
        "4": "% Disb Amt",
      },
      subRelation: {
        "0": " ",
        "1": "New-to-KPB",
        "2": "Existing-to-KPB",
        "3": "Past-KPB",
      },
    };
    return mappings[columnId]?.[value] || value;
  };

  const getRowStyle = (row: any, rowIdx: number) => {
    const highlight =
      row.cells.some(
        (c: any) =>
          (c.column.id === "category" || c.column.id === "state") &&
          c.value !== "0"
      ) ||
      (type === "1" && rowIdx % 2 === 1);
    return {
      backgroundColor: highlight ? "#e9ecef" : undefined,
      color: highlight && type === "1" ? "#212529" : undefined,
    };
  };

  const getBranchIdForColumn = (colIdx: number): string | null => {
    if (!isComparative || colIdx < fixedColumnCount || !branchIds.length)
      return null;
    const idx = (colIdx - fixedColumnCount) % branchCount;
    return branchIds[idx] ?? null;
  };

  const getCellStyle = (
    isHeader: boolean,
    colIdx: number
  ): React.CSSProperties => {
    const base = isHeader ? styles.headerCell : styles.bodyCell;
    if (isHeader || !isComparative) return base;

    const branchId = getBranchIdForColumn(colIdx);
    const c = branchId ? branchColors[branchId] : undefined;
    if (!c) return base;

    return {
      ...base,
      backgroundColor: c.bgColor,
      borderLeft: `3px solid ${c.borderColor}`,
    };
  };

  return (
    <div>
      <h1 style={styles.tableHead}>{tableHead}</h1>
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Loader />
        </div>
      ) : data.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table
            {...getTableProps()}
            className={`tableClass${type}`}
            style={styles.table}
          >
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()} style={styles.headerRow}>
                  {hg.headers.map((col, colIdx) => (
                    <th
                      {...col.getHeaderProps()}
                      style={getCellStyle(true, colIdx)}
                    >
                      {col.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, rIdx) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} style={getRowStyle(row, rIdx)}>
                    {row.cells.map((cell, cIdx) => {
                      const mapped = getMappedValue(cell.column.id, cell.value);
                      return (
                        <td
                          {...cell.getCellProps()}
                          style={getCellStyle(false, cIdx)}
                        >
                          {cell.render("Cell", { value: mapped })}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No data available for the selected criteria</p>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
    margin: "20px",
    border: "1px solid #ccc",
  },
  headerRow: {
    background: "#778da9",
    color: "#fff",
    borderBottom: "1px solid #bbb",
  },
  headerCell: {
    padding: "10px",
    textAlign: "center",
    fontSize: "small",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    border: "1px solid #bbb",
  },
  bodyCell: {
    padding: "10px",
    textAlign: "center",
    fontSize: "small",
    whiteSpace: "nowrap",
    border: "1px solid rgba(0,0,0,0.35)",
  },
  tableHead: {
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "Arial",
    textAlign: "start",
    paddingLeft: "15px",
    color: "#14213d",
    marginBottom: "10px",
  },
};

export default Table3;
